import { springs } from "@/components/ui/motion";
import type { AuditChecklistStats, UIAuditCategory } from "@/packages/types";
import { cn, useTranslation } from "@/packages/utils";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { BarChart3 } from "lucide-react";
import { useEffect } from "react";
import type { WidgetVariant } from "./WidgetCard";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface AuditProgressCardProps {
  categories: UIAuditCategory[];
  checklistStats: AuditChecklistStats;
  onNavigateToCategory: (categoryId: string) => void;
  index?: number;
  variant?: WidgetVariant;
}

const RADIUS = 40;
const STROKE_WIDTH = 7;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SVG_SIZE = (RADIUS + STROKE_WIDTH) * 2;
const CENTER = SVG_SIZE / 2;

function getBarColor(percent: number): { bg: string; fill: string } {
  if (percent > 80) return { bg: "bg-emerald-400/20", fill: "bg-emerald-500" };
  if (percent >= 40) return { bg: "bg-amber-400/20", fill: "bg-amber-500" };
  return { bg: "bg-red-400/20", fill: "bg-red-500" };
}

interface CategoryStat {
  id: string;
  name: string;
  completed: number;
  total: number;
  percent: number;
}

function deriveCategoryStats(categories: UIAuditCategory[]): CategoryStat[] {
  return categories
    .map((cat) => {
      const total = cat.items.length;
      // Count only APPROVED items as completed — consistent with donut center
      // which uses checklistStats.completed (APPROVED only)
      const completed = cat.items.filter(
        (item) => item.status === "APPROVED",
      ).length;
      return {
        id: cat.id,
        name: cat.name,
        completed,
        total,
        percent: total > 0 ? (completed / total) * 100 : 0,
      };
    })
    .sort((a, b) => {
      const aIncomplete = a.total - a.completed;
      const bIncomplete = b.total - b.completed;
      return bIncomplete - aIncomplete;
    });
}

function AnimatedPercent({ value }: { value: number }) {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { damping: 30, stiffness: 200 });
  const display = useTransform(spring, (v) => `${Math.round(v)}%`);

  useEffect(() => {
    mv.set(value);
  }, [mv, value]);

  return (
    <motion.span className="text-2xl font-bold tabular-nums tracking-tight text-primary">
      {display}
    </motion.span>
  );
}

export function AuditProgressCard({
  categories,
  checklistStats,
  onNavigateToCategory,
  index,
  variant,
}: AuditProgressCardProps) {
  const { t } = useTranslation("audits");
  const percent =
    checklistStats.total > 0
      ? Math.round((checklistStats.completed / checklistStats.total) * 100)
      : 0;
  const offset = CIRCUMFERENCE * (1 - percent / 100);
  const categoryStats = deriveCategoryStats(categories);
  const visibleCategories = categoryStats.slice(0, 5);
  const hiddenCount = categoryStats.length - 5;

  return (
    <WidgetCard
      size="M"
      title={t("overview.progress.title")}
      icon={<BarChart3 size={16} />}
      index={index}
      variant={variant}
    >
      {categories.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <span className="text-sm text-muted">
            {t("overview.progress.noCategories")}
          </span>
        </div>
      ) : (
        <div className="flex gap-5 h-full items-center">
          {/* Left column — SVG Donut */}
          <div className="flex flex-col items-center justify-center shrink-0">
            <div className="relative">
              <svg
                viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                className="w-28 h-28"
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <defs>
                  <linearGradient
                    id="donut-gradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#4a7c65" />
                    <stop offset="100%" stopColor="#6db892" />
                  </linearGradient>
                  <filter id="donut-glow">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite
                      in="SourceGraphic"
                      in2="blur"
                      operator="over"
                    />
                  </filter>
                </defs>
                {/* Track */}
                <circle
                  cx={CENTER}
                  cy={CENTER}
                  r={RADIUS}
                  fill="none"
                  strokeWidth={STROKE_WIDTH}
                  className="stroke-border/60"
                />
                {/* Progress */}
                <motion.circle
                  cx={CENTER}
                  cy={CENTER}
                  r={RADIUS}
                  fill="none"
                  strokeWidth={STROKE_WIDTH}
                  strokeLinecap="round"
                  stroke="url(#donut-gradient)"
                  strokeDasharray={CIRCUMFERENCE}
                  initial={{ strokeDashoffset: CIRCUMFERENCE }}
                  animate={{ strokeDashoffset: offset }}
                  transition={{ ...springs.smooth, delay: 0.3 }}
                  transform={`rotate(-90 ${CENTER} ${CENTER})`}
                  filter={percent > 0 ? "url(#donut-glow)" : undefined}
                />
              </svg>
              {/* Center text overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <AnimatedPercent value={percent} />
                <span className="text-[10px] text-muted leading-none mt-0.5">
                  {checklistStats.completed}/{checklistStats.total}
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="w-px self-stretch bg-border/50 my-1" />

          {/* Right column — Category Breakdown */}
          <div className="flex flex-col gap-2 flex-1 min-w-0 overflow-y-auto py-0.5 pr-0.5">
            {visibleCategories.map((cat, i) => {
              const colors = getBarColor(cat.percent);
              return (
                <button
                  type="button"
                  key={cat.id}
                  className="group flex flex-col gap-1 cursor-pointer hover:bg-surface/60 rounded-lg py-1.5 px-2 -mx-2 transition-colors"
                  onClick={() => onNavigateToCategory(cat.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-secondary truncate">
                      {cat.name}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-[11px] text-muted tabular-nums">
                        {cat.completed}/{cat.total}
                      </span>
                      <ChevronRight
                        size={11}
                        className="text-muted opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  </div>
                  <div className={cn("h-1.5 rounded-full w-full", colors.bg)}>
                    <motion.div
                      className={cn("h-full rounded-full", colors.fill)}
                      initial={{ width: 0 }}
                      animate={{ width: `${cat.percent}%` }}
                      transition={{ ...springs.smooth, delay: 0.3 + i * 0.06 }}
                    />
                  </div>
                </button>
              );
            })}
            {hiddenCount > 0 && (
              <span className="text-[11px] text-muted pl-2">
                {t("overview.progress.hiddenMore", { count: hiddenCount })}
              </span>
            )}
          </div>
        </div>
      )}
    </WidgetCard>
  );
}

export function AuditProgressCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="M" index={index} />;
}
